import { type ReactNode, createContext, useContext } from "react";

import { cn } from "@/lib/utils";

import { Skeleton } from "./ui/skeleton";

const LoadingContext = createContext(false);

const CardContainer = ({
  isLoading = false,
  children,
}: {
  showBorder?: boolean;
  isLoading?: boolean;
  children: ReactNode;
}) => (
  <LoadingContext.Provider value={isLoading}>
    {children}
  </LoadingContext.Provider>
);

const Header = ({
  children,
  headerContentClassName,
  headerClassName,
}: {
  children: ReactNode;
  headerContentClassName?: string;
  headerClassName?: string;
}) => (
  <div className={cn("mb-6 flex items-center", headerClassName)}>
    <div
      className={cn("flex items-center flex-1 gap-x-4", headerContentClassName)}
    >
      {children}
    </div>
  </div>
);

const HeaderContent = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-col items-start">{children}</div>
);

const CardTitle = ({ children }: { children: ReactNode }) => {
  const isLoading = useContext(LoadingContext);

  return isLoading ? (
    <Skeleton className="h-7 w-40 md:w-60" />
  ) : (
    <h1 className="text-2xl font-semibold tracking-tight capitalize">
      {children}
    </h1>
  );
};

const CardDescription = ({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) => {
  const isLoading = useContext(LoadingContext);

  return isLoading ? (
    <Skeleton className="mt-1 h-4 w-32 md:w-48" />
  ) : (
    <p className={cn("text-muted-foreground mt-1", className)}>{children}</p>
  );
};

const Icon = ({ children }: { children: ReactNode }) => {
  const isLoading = useContext(LoadingContext);
  return (
    <div className="flex items-center">
      {isLoading ? <Skeleton className="h-10 w-10 rounded-none" /> : children}
    </div>
  );
};

const TopRightContent = ({ children }: { children: ReactNode }) => {
  const isLoading = useContext(LoadingContext);
  return (
    <div className="ml-auto rtl:ml-0 rtl:mr-auto flex items-center">
      {isLoading ? <Skeleton className="h-9 w-10 md:w-20" /> : children}
    </div>
  );
};

const Content = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div className={cn("bg-card rounded-lg p-4 md:p-6 shadow-xs", className)}>
    {children}
  </div>
);

CardContainer.Header = Header;
CardContainer.HeaderContent = HeaderContent;
CardContainer.Icon = Icon;
CardContainer.TopRightContent = TopRightContent;
CardContainer.Content = Content;
CardContainer.CardTitle = CardTitle;
CardContainer.CardDescription = CardDescription;

export { CardContainer };
